// Dialer monitor, DNC, callbacks.
import * as React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Upload, Plus, Trash2, Search } from 'lucide-react';
import { api, errorText } from '@/lib/api';
import { PageHeader, DataTable, Pager, ErrorBox, Field, Loading } from '@/components/common';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input, Select } from '@/components/ui/input';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { StatusBadge, Badge } from '@/components/ui/badge';
import { CampaignStatusButtons } from './Config';
import { useRefOptions, rowLabel } from '@/hooks/meta';
import { toast } from '@/store/toast';
import { useAuth, hasRole, useCan } from '@/store/auth';
import { fmtTime, enumLabel } from '@/lib/utils';
import { t } from '@/i18n';
import { useLiveRefresh, usePoll } from '@/hooks/live';

const entries = (v: unknown): [string, unknown][] => (v && typeof v === 'object' && !Array.isArray(v) ? Object.entries(v) : []);

// a value of the dialer's status: rates as %, times in local time, codes as words, the rest as it is
function kvValue(k: string, v: unknown): string {
  if (/(_rate|^max_abandon)$/.test(k) && v !== '' && !Number.isNaN(Number(v))) return `${(Number(v) * 100).toFixed(1)}%`;
  if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}/.test(v)) return fmtTime(v);
  if (typeof v === 'string' && /^[A-Z][A-Z0-9_]+$/.test(v)) return enumLabel(v);
  if (Array.isArray(v)) return v.map(String).join(', ');
  return String(v);
}

// key / value lines that wrap inside their card; objects inside are shown as their own indented lines
function KV({ obj }: { obj: unknown }) {
  const e = entries(obj).filter(([, v]) => v !== undefined && v !== null && v !== '');
  if (!e.length) return <span className="text-muted-foreground">-</span>;
  return (
    <dl className="grid min-w-0 grid-cols-[minmax(0,auto)_minmax(0,1fr)] gap-x-3 gap-y-0.5 text-xs">
      {e.map(([k, v]) => (
        <React.Fragment key={k}>
          <dt className="break-words text-muted-foreground">{enumLabel(k)}</dt>
          <dd className="m-0 min-w-0 break-words font-medium">
            {v && typeof v === 'object' && !Array.isArray(v) ? <div className="border-l pl-2"><KV obj={v} /></div> : kvValue(k, v)}
          </dd>
        </React.Fragment>
      ))}
    </dl>
  );
}

// ---- Dialer -------------------------------------------------------------------------------------
export function DialerPage() {
  const campaigns = useQuery({ queryKey: ['campaigns-all'], queryFn: () => api<{ rows: any[] }>('/campaigns', { query: { limit: 200 } }), refetchInterval: 10000 });
  return (
    <div>
      <PageHeader title={t('Dialer')} description={t("What each campaign's dialer is doing. Details shows contacts by status, the whole last cycle and the predictive plan.")} />
      <ErrorBox error={campaigns.error} />
      <div className="grid gap-4 xl:grid-cols-2">
        {(campaigns.data?.rows ?? []).filter((c) => c.status !== 'ARCHIVED').map((c) => <DialerCard key={c.id} c={c} onChange={() => campaigns.refetch()} />)}
      </div>
    </div>
  );
}

function Tile({ label, value }: { label: string; value: React.ReactNode }) {
  return <div className="rounded-md border px-2 py-1"><div className="text-xs text-muted-foreground">{label}</div><div className="text-lg font-semibold leading-tight">{value}</div></div>;
}

// Compact by default: three numbers and one line for the last cycle; Details shows everything
// (contacts by status, the whole dialer_stats row, the predictive plan).
function DialerCard({ c, onChange }: { c: any; onChange: () => void }) {
  const poll = usePoll(5000);
  const st = useQuery({ queryKey: ['campaign-status', c.id], queryFn: () => api(`/campaigns/${c.id}/status`), refetchInterval: poll });
  useLiveRefresh(['dialer.updated', 'campaign.updated', 'contact.updated'], (e) => { if (Number(e.data.campaign_id) === Number(c.id)) st.refetch(); });
  const [details, setDetails] = React.useState(false);
  const d = st.data?.dialer ?? {};
  const contacts = st.data?.contacts?.contacts ?? {};
  const left = Number(contacts.NEW ?? 0) + Number(contacts.RETRY ?? 0);
  const lc = d.last_cycle ?? {};
  const cycle = lc.last_cycle_at
    ? [new Date(String(lc.last_cycle_at).replace(' ', 'T') + 'Z').toLocaleTimeString(),
       t('dialed {{dialed}} of {{ready}} ready', { dialed: lc.last_dialed ?? 0, ready: lc.last_ready ?? 0 }),
       lc.dial_ratio ? t('ratio {{ratio}}', { ratio: lc.dial_ratio }) : '',
       lc.last_reason ? enumLabel(lc.last_reason).toLowerCase() : ''].filter(Boolean).join(' · ')
    : t('no cycle yet');
  return (
    <Card data-testid={`dialer-${c.id}`}>
      <CardHeader className="flex-row items-start justify-between pb-2">
        <div><CardTitle>{c.name}</CardTitle><CardDescription>{enumLabel(c.dial_method)}</CardDescription></div>
        <div className="flex items-center gap-1"><StatusBadge value={c.status} /><CampaignStatusButtons campaign={c} onDone={() => { onChange(); st.refetch(); }} /></div>
      </CardHeader>
      <CardContent className="grid gap-2 text-sm">
        {st.isLoading ? <Loading /> : <>
          <ErrorBox error={st.error} />
          <div className="grid grid-cols-3 gap-2">
            <Tile label={t('Ready agents')} value={d.ready_agents ?? 0} />
            <Tile label={t('Active calls')} value={d.active_calls ?? st.data?.contacts?.active_calls ?? 0} />
            <Tile label={t('Contacts left')} value={left} />
          </div>
          <div className="flex items-center justify-between gap-2 text-xs text-muted-foreground">
            <span data-testid={`dialer-${c.id}-cycle`}>{t('Last cycle: {{cycle}}', { cycle })}</span>
            <Button size="sm" variant="ghost" className="h-6 px-2 text-xs" aria-expanded={details} onClick={() => setDetails((v) => !v)}>{details ? t('Hide details') : t('Details')}</Button>
          </div>
          {details && <div className="grid gap-2 border-t pt-2">
            <div className="text-xs text-muted-foreground">{t('max {{cps}} CPS · {{concurrent}} concurrent', { cps: c.max_cps ?? '-', concurrent: c.max_concurrent ?? '-' })}</div>
            <div><div className="mb-1 text-xs text-muted-foreground">{t('Contacts')}</div><KV obj={contacts} /></div>
            <div><div className="mb-1 text-xs text-muted-foreground">{t('Last cycle')}</div><KV obj={{ ...lc, campaign_id: undefined }} /></div>
            {st.data?.plan && <div><div className="mb-1 text-xs text-muted-foreground">{t('Predictive plan')}</div><KV obj={{ ...st.data.plan, ok: undefined, campaign_id: undefined }} /></div>}
          </div>}
        </>}
      </CardContent>
    </Card>
  );
}

// ---- DNC ----------------------------------------------------------------------------------------
export function DncPage() {
  const isAdmin = useAuth((s) => hasRole(s.user?.role, ['ADMIN']));
  const [canAdd, canImport, canDelete] = [useCan('dnc.add'), useCan('dnc.import'), useCan('dnc.delete')];
  const [q, setQ] = React.useState('');
  const [scope, setScope] = React.useState('');
  const [offset, setOffset] = React.useState(0);
  const [adding, setAdding] = React.useState(false);
  const [importing, setImporting] = React.useState(false);
  const [check, setCheck] = React.useState('');
  const [checkRes, setCheckRes] = React.useState<any>(null);
  const query = { q, scope, active: 1, limit: 25, offset };
  const rows = useQuery({ queryKey: ['dnc', query], queryFn: () => api<{ rows: any[]; total: number }>('/dnc', { query }) });
  async function remove(r: any) {
    if (!confirm(t('Remove {{number}} from the DNC list?', { number: r.phone_number }))) return;
    try { await api(`/dnc/${r.id}`, { method: 'DELETE' }); toast.ok(t('Removed')); rows.refetch(); } catch (e) { toast.error(errorText(e)); }
  }
  async function doCheck(e: React.FormEvent) {
    e.preventDefault();
    try { setCheckRes(await api('/dnc/check', { query: { number: check } })); } catch (err) { toast.error(errorText(err)); }
  }
  return (
    <div>
      <PageHeader title={t('DNC')} description={t('Numbers that must never be dialed. Checked again by the engine right before every call.')}>
        {canImport && <Button variant="outline" onClick={() => setImporting(true)}><Upload /> {t('Import')}</Button>}
        {canAdd && <Button onClick={() => setAdding(true)}><Plus /> {t('Add')}</Button>}
      </PageHeader>
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <div className="relative w-56"><Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input className="pl-8" placeholder={t('Search number…')} value={q} onChange={(e) => { setQ(e.target.value); setOffset(0); }} /></div>
        <Select className="w-40" value={scope} onChange={(e) => setScope(e.target.value)}><option value="">{t('All scopes')}</option>{['GLOBAL', 'TENANT', 'CAMPAIGN'].map((s) => <option key={s} value={s}>{enumLabel(s)}</option>)}</Select>
        <form onSubmit={doCheck} className="ml-auto flex items-center gap-2">
          <Input className="w-44" placeholder={t('Check a number')} value={check} onChange={(e) => setCheck(e.target.value)} />
          <Button variant="outline" type="submit">{t('Check')}</Button>
          {checkRes && (checkRes.dnc ? <Badge variant="destructive">{t('blocked ({{scope}})', { scope: enumLabel(checkRes.scope) })}</Badge> : <Badge variant="success">{t('not listed')}</Badge>)}
        </form>
      </div>
      <ErrorBox error={rows.error} />
      <DataTable rows={rows.data?.rows} columns={[
        { key: 'phone_number', label: t('Number') }, { key: 'scope', label: t('Scope'), render: (r) => <StatusBadge value={r.scope} /> },
        { key: 'campaign_id', label: t('Campaign') }, { key: 'reason', label: t('Reason') }, { key: 'source', label: t('Source') },
        { key: 'added_by', label: t('Added by') }, { key: 'expires_at', label: t('Expires'), render: (r) => r.expires_at ? fmtTime(r.expires_at) : t('never') },
        { key: 'created_at', label: t('Added'), render: (r) => fmtTime(r.created_at) }]}
        actions={(r) => canDelete && (r.scope !== 'GLOBAL' || isAdmin) ? <Button size="icon" variant="ghost" aria-label={t('Remove')} onClick={() => remove(r)}><Trash2 className="text-destructive" /></Button> : null} />
      <Pager offset={offset} limit={25} total={rows.data?.total} onChange={setOffset} />
      <Dialog open={adding} onOpenChange={setAdding}>
        {adding && <DialogContent title={t('Add a DNC number')}><DncAdd onDone={() => { setAdding(false); rows.refetch(); }} /></DialogContent>}
      </Dialog>
      <Dialog open={importing} onOpenChange={setImporting}>
        {importing && <DialogContent title={t('Import DNC numbers')} description={t('A CSV or text file; the first column of each line is the number.')}><DncImport onDone={() => { setImporting(false); rows.refetch(); }} /></DialogContent>}
      </Dialog>
    </div>
  );
}

function ScopeFields({ scope, setScope, campaign, setCampaign }: { scope: string; setScope: (s: string) => void; campaign: string; setCampaign: (s: string) => void }) {
  const isAdmin = useAuth((s) => hasRole(s.user?.role, ['ADMIN']));
  const campaigns = useRefOptions('campaigns');
  return (<>
    <Field help="dnc.scope" label={t('Scope')}><Select value={scope} onChange={(e) => setScope(e.target.value)}>
      <option value="TENANT">{t('Whole company')}</option><option value="CAMPAIGN">{t('One campaign')}</option>{isAdmin && <option value="GLOBAL">{t('Global (all tenants)')}</option>}
    </Select></Field>
    {scope === 'CAMPAIGN' && <Field help="dnc.campaign" label={t('Campaign')}><Select value={campaign} onChange={(e) => setCampaign(e.target.value)} required>
      <option value="">{t('Choose…')}</option>{(campaigns.data ?? []).map((c) => <option key={c.id} value={c.id}>{rowLabel(c)}</option>)}
    </Select></Field>}
  </>);
}

function DncAdd({ onDone }: { onDone: () => void }) {
  const [number, setNumber] = React.useState('');
  const [scope, setScope] = React.useState('TENANT');
  const [campaign, setCampaign] = React.useState('');
  const [reason, setReason] = React.useState('');
  const [days, setDays] = React.useState('');
  const [error, setError] = React.useState<unknown>(null);
  async function submit(e: React.FormEvent) {
    e.preventDefault(); setError(null);
    try {
      await api('/dnc', { body: { number, scope, campaign_id: campaign ? Number(campaign) : undefined, reason: reason || undefined, expires_sec: days ? Number(days) * 86400 : undefined } });
      toast.ok(t('Added')); onDone();
    } catch (err) { setError(err); }
  }
  return (
    <form onSubmit={submit} className="grid gap-3">
      <Field help="dnc.number" label={t('Number')}><Input value={number} onChange={(e) => setNumber(e.target.value)} required /></Field>
      <ScopeFields scope={scope} setScope={setScope} campaign={campaign} setCampaign={setCampaign} />
      <Field help="dnc.reason" label={t('Reason')}><Input value={reason} onChange={(e) => setReason(e.target.value)} /></Field>
      <Field help="dnc.expires_days" label={t('Expires after (days)')} hint={t('Leave empty to keep it forever.')}><Input type="number" min={1} value={days} onChange={(e) => setDays(e.target.value)} /></Field>
      <ErrorBox error={error} />
      <div className="flex justify-end"><Button type="submit">{t('Add')}</Button></div>
    </form>
  );
}

function DncImport({ onDone }: { onDone: () => void }) {
  const [file, setFile] = React.useState<File | null>(null);
  const [scope, setScope] = React.useState('TENANT');
  const [campaign, setCampaign] = React.useState('');
  const [res, setRes] = React.useState<any>(null);
  const [error, setError] = React.useState<unknown>(null);
  async function go() {
    if (!file) return;
    setError(null);
    try { setRes(await api('/dnc/import', { query: { scope, campaign_id: campaign }, raw: await file.text(), contentType: 'text/csv' })); }
    catch (e) { setError(e); }
  }
  return (
    <div className="grid gap-3">
      <Input type="file" accept=".csv,.txt,text/csv,text/plain" onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
      <ScopeFields scope={scope} setScope={setScope} campaign={campaign} setCampaign={setCampaign} />
      <ErrorBox error={error} />
      {res && <div className="rounded-md bg-muted p-3 text-sm">{t('Added {{added}}, already listed {{listed}}, invalid {{invalid}}, duplicates in file {{dups}}.', { added: res.added, listed: res.already_listed, invalid: res.invalid, dups: res.duplicates_in_file })}</div>}
      <div className="flex justify-end gap-2"><Button variant="outline" onClick={onDone}>{t('Close')}</Button><Button onClick={go} disabled={!file}>{t('Import')}</Button></div>
    </div>
  );
}

// ---- Callbacks -----------------------------------------------------------------------------------
export function CallbacksPage() {
  const [status, setStatus] = React.useState('');
  const [campaign, setCampaign] = React.useState('');
  const [offset, setOffset] = React.useState(0);
  const [adding, setAdding] = React.useState(false);
  const [resched, setResched] = React.useState<any | null>(null);
  const campaigns = useRefOptions('campaigns');
  const query = { status, campaign_id: campaign, limit: 25, offset };
  const rows = useQuery({ queryKey: ['callbacks', query], queryFn: () => api<{ rows: any[]; total: number }>('/callbacks', { query }), refetchInterval: 10000 });
  async function cancel(r: any) {
    if (!confirm(t('Cancel this callback?'))) return;
    try { await api(`/callbacks/${r.id}/cancel`, { method: 'POST' }); toast.ok(t('Cancelled')); rows.refetch(); } catch (e) { toast.error(errorText(e)); }
  }
  const [canAdd, canEdit] = [useCan('callbacks.add'), useCan('callbacks.edit')];
  const open = (r: any) => canEdit && ['PENDING', 'DUE', 'MISSED'].includes(r.status);
  return (
    <div>
      <PageHeader title={t('Callbacks')} description={t('Scheduled calls back to customers, for one agent or for anyone in the campaign.')}>
        {canAdd && <Button onClick={() => setAdding(true)}><Plus /> {t('Schedule')}</Button>}
      </PageHeader>
      <div className="mb-3 flex gap-2">
        <Select className="w-44" value={status} onChange={(e) => setStatus(e.target.value)}><option value="">{t('All statuses')}</option>
          {['PENDING', 'DUE', 'IN_PROGRESS', 'COMPLETED', 'MISSED', 'CANCELLED', 'EXPIRED', 'FAILED'].map((s) => <option key={s} value={s}>{enumLabel(s)}</option>)}</Select>
        <Select className="w-56" value={campaign} onChange={(e) => setCampaign(e.target.value)}><option value="">{t('All campaigns')}</option>
          {(campaigns.data ?? []).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</Select>
      </div>
      <ErrorBox error={rows.error} />
      <DataTable rows={rows.data?.rows} columns={[
        { key: 'callback_time', label: t('When'), render: (r) => fmtTime(r.callback_time) }, { key: 'phone_number', label: t('Number') },
        { key: 'first_name', label: t('Name'), render: (r) => `${r.first_name ?? ''} ${r.last_name ?? ''}`.trim() || '-' },
        { key: 'agent_code', label: t('Agent'), render: (r) => r.agent_code ?? <Badge variant="outline">{t('anyone')}</Badge> },
        { key: 'type', label: t('Type'), render: (r) => <StatusBadge value={r.type} /> }, { key: 'status', label: t('Status'), render: (r) => <StatusBadge value={r.status} /> },
        { key: 'attempt_count', label: t('Tries') }, { key: 'last_result', label: t('Last result') }, { key: 'notes', label: t('Notes') }]}
        actions={(r) => open(r) ? (<>
          <Button size="sm" variant="ghost" onClick={() => setResched(r)}>{t('Reschedule')}</Button>
          <Button size="sm" variant="ghost" onClick={() => cancel(r)}>{t('Cancel')}</Button>
        </>) : null} />
      <Pager offset={offset} limit={25} total={rows.data?.total} onChange={setOffset} />
      <Dialog open={adding} onOpenChange={setAdding}>{adding && <DialogContent title={t('Schedule a callback')}><CallbackForm onDone={() => { setAdding(false); rows.refetch(); }} /></DialogContent>}</Dialog>
      <Dialog open={!!resched} onOpenChange={(o) => !o && setResched(null)}>{resched && <DialogContent title={t('Reschedule callback')}><CallbackForm existing={resched} onDone={() => { setResched(null); rows.refetch(); }} /></DialogContent>}</Dialog>
    </div>
  );
}

function CallbackForm({ existing, onDone }: { existing?: any; onDone: () => void }) {
  const campaigns = useRefOptions('campaigns');
  const agents = useRefOptions('agents');
  const [campaign, setCampaign] = React.useState('');
  const [number, setNumber] = React.useState('');
  const [when, setWhen] = React.useState('');
  const [agent, setAgent] = React.useState(existing?.agent_id ? String(existing.agent_id) : '');
  const [notes, setNotes] = React.useState('');
  const [error, setError] = React.useState<unknown>(null);
  async function submit(e: React.FormEvent) {
    e.preventDefault(); setError(null);
    const inSec = when ? Math.max(0, Math.round((new Date(when).getTime() - Date.now()) / 1000)) : 0;
    try {
      if (existing) await api(`/callbacks/${existing.id}/reschedule`, { body: { in_sec: inSec, agent_id: agent ? Number(agent) : null } });
      else await api('/callbacks', { body: { campaign_id: Number(campaign), number, in_sec: inSec, agent_id: agent ? Number(agent) : undefined, notes: notes || undefined } });
      toast.ok(existing ? t('Rescheduled') : t('Scheduled')); onDone();
    } catch (err) { setError(err); }
  }
  return (
    <form onSubmit={submit} className="grid gap-3">
      {!existing && <>
        <Field help="callbacks.campaign" label={t('Campaign')}><Select value={campaign} onChange={(e) => setCampaign(e.target.value)} required><option value="">{t('Choose…')}</option>
          {(campaigns.data ?? []).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</Select></Field>
        <Field help="callbacks.number" label={t('Number')}><Input value={number} onChange={(e) => setNumber(e.target.value)} required /></Field>
      </>}
      <Field help="callbacks.when" label={t('When')} hint={t('Your local time. Empty means now.')}><Input type="datetime-local" value={when} onChange={(e) => setWhen(e.target.value)} /></Field>
      <Field help="callbacks.agent" label={t('Agent')}><Select value={agent} onChange={(e) => setAgent(e.target.value)}><option value="">{t('Anyone in the campaign')}</option>
        {(agents.data ?? []).map((a) => <option key={a.id} value={a.id}>{rowLabel(a)}</option>)}</Select></Field>
      {!existing && <Field help="callbacks.notes" label={t('Notes')}><Input value={notes} onChange={(e) => setNotes(e.target.value)} /></Field>}
      <ErrorBox error={error} />
      <div className="flex justify-end"><Button type="submit">{existing ? t('Reschedule') : t('Schedule')}</Button></div>
    </form>
  );
}
